/**
 * Product Customization Form Handler
 * 
 * Reemplaza el sistema de secuestro AJAX con un enfoque de inputs dinámicos
 * que permite que el formulario se envíe normalmente y sea detectado por
 * sistemas de carrito de terceros.
 * 
 * En lugar de interceptar el click y hacer AJAX, este sistema:
 * 1. Habilita/deshabilita inputs dinámicos según el estado de personalización
 * 2. Sincroniza valores entre inputs base y addon
 * 3. Deja que el formulario se envíe normalmente
 */

class ProductCustomizationFormHandler {
  constructor() {
    this.isCustomizationActive = false;
    this.isFreeCustomization = true;
    this.form = null;
    this.addonInputs = [];
    this.addonPropertyInputs = [];
    this.basePropertyInputs = {};
    this.quickBuyContainer = null;
    this.variantUpdateTimer = null;
    this._boundHandlers = {};

    this._init();
  }

  _init() {
    // Buscar elementos del DOM
    this._findElements();

    // Configurar event listeners
    this._setupEventListeners();

    // Determinar si la personalización es gratuita
    this._checkCustomizationMode();

    // Inicializar el parche de equipo si está habilitado
    this._initializeTeamPatch();
  }

  _findElements() {
    const personalizerBlock = document.querySelector('[data-block-type="personalizador_camisetas"]');
    const sectionScope = personalizerBlock
      ? (personalizerBlock.closest('.shopify-section, [id^="shopify-section-"]') || document)
      : document;

    const addonProductInput = sectionScope.querySelector('#addon_property_producto');
    this.form = addonProductInput ? addonProductInput.closest('form') : null;
    if (!this.form) {
      this.form = document.querySelector('form[data-type="add-to-cart-form"]');
    }

    // Buscar todos los inputs del addon (disabled por defecto)
    this.addonInputs = this.form ? Array.from(this.form.querySelectorAll('.customization-addon-input')) : [];
    this.addonPropertyInputs = this.form ? Array.from(this.form.querySelectorAll('.customization-addon-property')) : [];
    
    // Buscar inputs base de propiedades
    this.basePropertyInputs = {
      nombre: this.form ? this.form.querySelector('#product_form_player_name') : null,
      dorsal: this.form ? this.form.querySelector('#product_form_player_number') : null,
      opciones: this.form ? this.form.querySelector('#product_form_customization_options') : null,
      parche: this.form ? this.form.querySelector('#product_form_team_patch') : null
    };

    // Buscar contenedor de Quick Buy
    this.quickBuyContainer = this.form ? this.form.querySelector('.accelerated-checkout-block') : null;
  }

  _setupEventListeners() {
    this._boundHandlers.syncAddonProperties = this._boundHandlers.syncAddonProperties || (() => this._syncAddonProperties());
    this._boundHandlers.customizationTypeChange =
      this._boundHandlers.customizationTypeChange || (() => this._handleCustomizationTypeChange());
    this._boundHandlers.syncVariantDependentInputs =
      this._boundHandlers.syncVariantDependentInputs || (() => this.syncVariantDependentInputs());
    this._boundHandlers.variantUpdate =
      this._boundHandlers.variantUpdate ||
      (() => {
        if (this.variantUpdateTimer) {
          clearTimeout(this.variantUpdateTimer);
        }

        // Esperar al final del ciclo actual para leer la variante final tras el morph.
        this.variantUpdateTimer = setTimeout(() => {
          this.syncVariantDependentInputs();
          this.variantUpdateTimer = null;
        }, 0);
      });
    this._boundHandlers.beforeSubmit = this._boundHandlers.beforeSubmit || ((event) => this._handleBeforeSubmit(event));

    // Escuchar cambios en los inputs base para sincronizar con addon
    if (this.basePropertyInputs.nombre) {
      this.basePropertyInputs.nombre.addEventListener('input', this._boundHandlers.syncAddonProperties);
    }
    if (this.basePropertyInputs.dorsal) {
      this.basePropertyInputs.dorsal.addEventListener('input', this._boundHandlers.syncAddonProperties);
    }
    if (this.basePropertyInputs.parche) {
      this.basePropertyInputs.parche.addEventListener('input', this._boundHandlers.syncAddonProperties);
      this.basePropertyInputs.parche.addEventListener('change', this._boundHandlers.syncAddonProperties);
    }

    // Escuchar cambios en el tipo de personalización
    const customizationTypeSelect = document.querySelector('#customization_type');
    if (customizationTypeSelect) {
      customizationTypeSelect.addEventListener('change', this._boundHandlers.customizationTypeChange);
    }

    // CRÍTICO: Crear el input parent_id desde el inicio
    this._createParentIdInput();

    // Escuchar cambios en la variante para actualizar parent_id y product info
    const variantIdInput = this.form ? this.form.querySelector('input[name="id"]') : null;
    if (variantIdInput) {
      // Actualizar parent_id y product info cuando cambia la variante
      const observer = new MutationObserver(this._boundHandlers.syncVariantDependentInputs);
      observer.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });

      // También escuchar cambios sobre la propiedad value del input.
      variantIdInput.addEventListener('change', this._boundHandlers.syncVariantDependentInputs);
      variantIdInput.addEventListener('input', this._boundHandlers.syncVariantDependentInputs);
    }

    document.addEventListener('variant:update', this._boundHandlers.variantUpdate);

    // Actualizar valores iniciales
    this.syncVariantDependentInputs();

    // Asegurar sincronización final justo antes del submit del formulario
    if (this.form) {
      this.form.addEventListener('submit', this._boundHandlers.beforeSubmit);
    }
  }

  reinit() {
    if (this.variantUpdateTimer) {
      clearTimeout(this.variantUpdateTimer);
      this.variantUpdateTimer = null;
    }

    if (this.form && this._boundHandlers.beforeSubmit) {
      this.form.removeEventListener('submit', this._boundHandlers.beforeSubmit);
    }

    if (this._boundHandlers.variantUpdate) {
      document.removeEventListener('variant:update', this._boundHandlers.variantUpdate);
    }

    this.form = null;
    this.addonInputs = [];
    this.addonPropertyInputs = [];
    this.basePropertyInputs = {};
    this.quickBuyContainer = null;
    this.parentIdInput = null;

    this._findElements();
    this._checkCustomizationMode();
    this._setupEventListeners();
    this._initializeTeamPatch();
    this._handleCustomizationTypeChange();
  }

  _checkCustomizationMode() {
    const personalizadorBlock = document.querySelector('[data-block-type="personalizador_camisetas"]');
    this.isFreeCustomization = personalizadorBlock && personalizadorBlock.dataset.freeCustomization === 'true';
  }

  /**
   * Inicializa el parche de equipo con el valor por defecto si está habilitado
   */
  _initializeTeamPatch() {
    const enableTeamPatchElement = document.querySelector('[data-enable-team-patch]');
    const enableTeamPatch = enableTeamPatchElement ? enableTeamPatchElement.dataset.enableTeamPatch === 'true' : false;

    if (!enableTeamPatch) {
      return;
    }

    // Verificar si ya hay un valor establecido
    const teamPatchField = this.basePropertyInputs.parche;
    if (teamPatchField && !teamPatchField.value) {
      // Establecer el valor por defecto (Parche Masculino)
      teamPatchField.value = 'Parche Masculino';
    }
  }

  /**
   * Crea el input parent_id en el DOM desde el inicio
   * Esto es necesario para que esté disponible cuando se construye el FormData
   */
  _createParentIdInput() {
    // Buscar si ya existe
    let parentIdInput = this.form
      ? this.form.querySelector('.customization-addon-input[name="items[1]parent_id"]')
      : null;
    if (parentIdInput) {
      this.parentIdInput = parentIdInput;
      return;
    }

    // Crear el input
    parentIdInput = document.createElement('input');
    parentIdInput.type = 'hidden';
    parentIdInput.className = 'customization-addon-input';
    parentIdInput.name = 'items[1]parent_id';
    parentIdInput.value = ''; // Se actualizará dinámicamente
    parentIdInput.disabled = true; // Deshabilitado por defecto

    // Insertar junto con los otros inputs del addon
    const addonIdInput = this.form
      ? this.form.querySelector('.customization-addon-input[name="items[1]id"]')
      : null;
    if (addonIdInput && addonIdInput.parentNode) {
      addonIdInput.parentNode.insertBefore(parentIdInput, addonIdInput.nextSibling);
    }

    // Guardar referencia
    this.parentIdInput = parentIdInput;
  }

  /**
   * Actualiza el valor del parent_id con el variant ID actual
   * Se llama cada vez que cambia la variante seleccionada
   */
  _updateParentId() {
    if (!this.parentIdInput) return;

    const baseVariantIdInput = this.form ? this.form.querySelector('input[name="id"]') : null;
    if (baseVariantIdInput && baseVariantIdInput.value) {
      const baseVariantId = baseVariantIdInput.value;
      this.parentIdInput.value = baseVariantId;
    }
  }

  _updatePatchAddonParentId() {
    if (!this.form) return;

    const patchParentIdInput = this.form.querySelector('#patch_addon_parent_id');
    const patchAddonVariantInput = this.form.querySelector('#patch_addon_variant_id');
    const baseVariantIdInput = this.form.querySelector('input[name="id"]');

    if (!patchParentIdInput || !patchAddonVariantInput) return;

    if (patchAddonVariantInput.value && baseVariantIdInput?.value) {
      patchParentIdInput.value = baseVariantIdInput.value;
    } else if (!patchAddonVariantInput.value) {
      patchParentIdInput.value = '';
    }
  }

  /**
   * Actualiza el campo "Producto" del addon con el nombre del producto + variante
   * Se llama cuando se habilita personalización o cuando cambia la variante
   */
  _updateProductInfo() {
    const addonProducto = this.form ? this.form.querySelector('#addon_property_producto') : null;
    if (!addonProducto) return;

    const productTitle = this._getBaseProductTitle();
    const variantTitle = this._getSelectedVariantTitle();

    addonProducto.value = productTitle && variantTitle
      ? `${productTitle} / ${variantTitle}`
      : (productTitle || '');

  }

  syncVariantDependentInputs() {
    this._updateParentId();
    this._updatePatchAddonParentId();
    this._updateProductInfo();
  }

  _getBaseProductTitle() {
    const fromSettings = window.personalizationProductSettings?.base_product_title;
    if (fromSettings && String(fromSettings).trim() !== '') {
      return String(fromSettings).trim();
    }

    const productTitleElement = document.querySelector('h1');
    return productTitleElement ? productTitleElement.textContent.trim() : '';
  }

  _getSelectedVariantTitle() {
    const variantIdInput = this.form ? this.form.querySelector('input[name="id"]') : null;
    const selectedVariantId = variantIdInput ? String(variantIdInput.value) : '';
    if (!selectedVariantId) return '';

    const variantJsonScripts = Array.from(document.querySelectorAll('variant-picker script[type="application/json"]'));
    for (const script of variantJsonScripts) {
      try {
        const parsed = JSON.parse(script.textContent || '');
        const variants = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.variants) ? parsed.variants : []);
        if (!variants.length) continue;

        const selected = variants.find((variant) => String(variant.id) === selectedVariantId);
        if (selected?.title) return String(selected.title).trim();
      } catch (e) {
        // Try next script
      }
    }

    const fromSettings = window.personalizationProductSettings?.base_product_variant;
    if (fromSettings && String(fromSettings).trim() !== '') {
      return String(fromSettings).trim();
    }

    const scope = this.form ? (this.form.closest('.shopify-section, [id^="shopify-section-"]') || document) : document;
    const activeSizeButton = scope.querySelector('.size-option.active');
    if (activeSizeButton?.dataset?.size) {
      return String(activeSizeButton.dataset.size).trim();
    }

    return '';
  }

  /**
   * Activa o desactiva la personalización
   * @param {boolean} isActive - Si la personalización está activa
   */
  setCustomizationActive(isActive) {
    this.isCustomizationActive = isActive;

    if (isActive) {
      this._enableCustomization();
    } else {
      this._disableCustomization();
    }
  }

  _enableCustomization() {
    if (!this.isFreeCustomization) {
      // PERSONALIZACIÓN DE PAGO
      // Deshabilitar inputs base (las propiedades van solo en el addon)
      this._disableBasePropertyInputs();

      // Habilitar inputs del addon
      this._updateParentId();
      this._updateProductInfo();
      this._enableAddonInputs();
      this._syncAddonProperties();

      // Habilitar el parche en el addon si está configurado
      this._enableTeamPatchForAddon();
    } else {
      // PERSONALIZACIÓN GRATIS
      // Habilitar inputs base (las propiedades van en el producto base)
      this._enableBasePropertyInputs();

      // Deshabilitar inputs del addon (no se usa addon en modo gratis)
      this._disableAddonInputs();

      // Habilitar el parche en el producto base si está configurado
      this._enableTeamPatchForBase();
    }

    // Ocultar Quick Buy
    this._hideQuickBuy();
  }

  _disableCustomization() {
    const patchValueBeforeDisable = this.basePropertyInputs.parche ? this.basePropertyInputs.parche.value : '';

    // Deshabilitar inputs del addon
    this._disableAddonInputs();

    // Deshabilitar inputs base
    this._disableBasePropertyInputs();

    // Limpiar valores base
    Object.entries(this.basePropertyInputs).forEach(([key, input]) => {
      if (!input) return;
      // Preservar el parche para no perder la selección entre cambios de estado internos.
      if (key === 'parche') return;
      input.value = '';
    });

    // Restaurar el valor por defecto del parche si está habilitado
    const enableTeamPatchElement = document.querySelector('[data-enable-team-patch]');
    const enableTeamPatch = enableTeamPatchElement ? enableTeamPatchElement.dataset.enableTeamPatch === 'true' : false;

    if (enableTeamPatch && this.basePropertyInputs.parche) {
      // Mantener selección actual; usar default solo cuando no haya valor.
      this.basePropertyInputs.parche.value =
        patchValueBeforeDisable || this.basePropertyInputs.parche.value || 'Parche Masculino';
      this.basePropertyInputs.parche.disabled = false;
      this.basePropertyInputs.parche.removeAttribute('disabled');
    }

    // Mostrar Quick Buy
    this._showQuickBuy();
  }

  _enableAddonInputs() {
    this.addonInputs.forEach(input => {
      input.disabled = false;
      input.removeAttribute('disabled');
    });

    // También habilitar el parent_id
    if (this.parentIdInput) {
      this.parentIdInput.disabled = false;
      this.parentIdInput.removeAttribute('disabled');
    }
  }

  _disableAddonInputs() {
    this.addonInputs.forEach(input => {
      input.disabled = true;
      input.setAttribute('disabled', 'disabled');
    });

    // También deshabilitar el parent_id
    if (this.parentIdInput) {
      this.parentIdInput.disabled = true;
      this.parentIdInput.setAttribute('disabled', 'disabled');
    }
  }

  _enableBasePropertyInputs() {
    Object.entries(this.basePropertyInputs).forEach(([key, input]) => {
      if (input) {
        input.disabled = false;
        input.removeAttribute('disabled');
      }
    });
  }

  _disableBasePropertyInputs() {
    Object.entries(this.basePropertyInputs).forEach(([key, input]) => {
      if (input) {
        input.disabled = true;
        input.setAttribute('disabled', 'disabled');
      }
    });
  }

  /**
   * Habilita el campo de parche de equipo en el producto base (personalización gratis)
   */
  _enableTeamPatchForBase() {
    const enableTeamPatchElement = document.querySelector('[data-enable-team-patch]');
    const enableTeamPatch = enableTeamPatchElement ? enableTeamPatchElement.dataset.enableTeamPatch === 'true' : false;

    if (!enableTeamPatch) {
      return;
    }

    const teamPatchField = this.basePropertyInputs.parche;
    if (teamPatchField) {
      // Habilitar el campo
      teamPatchField.disabled = false;
      teamPatchField.removeAttribute('disabled');

      // Asegurar que tenga el valor por defecto si está vacío
      if (!teamPatchField.value) {
        teamPatchField.value = 'Parche Masculino';
      }
    }
  }

  /**
   * Habilita el campo de parche de equipo en el addon (personalización de pago)
   */
  _enableTeamPatchForAddon() {
    const enableTeamPatchElement = document.querySelector('[data-enable-team-patch]');
    const enableTeamPatch = enableTeamPatchElement ? enableTeamPatchElement.dataset.enableTeamPatch === 'true' : false;

    if (!enableTeamPatch) {
      return;
    }

    const addonParche = document.querySelector('#addon_property_parche');
    const teamPatchField = this.basePropertyInputs.parche;

    if (addonParche && teamPatchField) {
      // Asegurar que el campo base tenga el valor por defecto
      if (!teamPatchField.value) {
        teamPatchField.value = 'Parche Masculino';
      }

      // Sincronizar el valor al addon
      addonParche.value = teamPatchField.value;
    }
  }

  /**
   * Sincroniza los valores de los inputs base con los del addon
   */
  _syncAddonProperties() {
    // Solo sincronizar si la personalización está activa y es de pago
    if (!this.isCustomizationActive || this.isFreeCustomization) {
      return;
    }

    // Sincronizar nombre
    const addonNombre = document.querySelector('#addon_property_nombre');
    if (addonNombre && this.basePropertyInputs.nombre) {
      addonNombre.value = this.basePropertyInputs.nombre.value;
    }

    // Sincronizar dorsal
    const addonDorsal = document.querySelector('#addon_property_dorsal');
    if (addonDorsal && this.basePropertyInputs.dorsal) {
      addonDorsal.value = this.basePropertyInputs.dorsal.value;
    }

    // Sincronizar parche
    const addonParche = document.querySelector('#addon_property_parche');
    if (addonParche && this.basePropertyInputs.parche) {
      addonParche.value = this.basePropertyInputs.parche.value;
    }

  }

  /**
   * Handler que se ejecuta antes del submit
   * Asegura que todo esté actualizado antes de enviar el formulario
   */
  _handleBeforeSubmit(event) {
    const addonIdInput = this.form ? this.form.querySelector('input[name="items[1]id"]') : null;
    const addonQtyInput = this.form ? this.form.querySelector('input[name="items[1]quantity"]') : null;
    const addonProducto = this.form ? this.form.querySelector('#addon_property_producto') : null;
    const shouldPrepareAddon =
      Boolean(addonIdInput && addonIdInput.value) &&
      Boolean(addonQtyInput && addonQtyInput.value) &&
      Boolean(addonIdInput && !addonIdInput.disabled);

    // Home puede desincronizar el estado interno; si el addon está realmente activo en el form,
    // forzamos actualización de parent_id + Producto base/variante + properties.
    if (shouldPrepareAddon) {
      this._updateParentId();
      this._updateProductInfo();
      if (addonProducto) {
        addonProducto.disabled = false;
        addonProducto.removeAttribute('disabled');
      }
      this._syncAddonProperties();
    }

    // Solo actuar si hay personalización activa y es de pago
    if (!this.isCustomizationActive || this.isFreeCustomization) {
      return;
    }

    // Asegurar que todo esté actualizado (por si acaso)
    this._updateParentId();
    this._updateProductInfo();
    this._syncAddonProperties();
  }

  _handleCustomizationTypeChange() {
    const customizationType = document.querySelector('#customization_type')?.value;
    
    // Determinar si hay personalización activa
    const hasCustomization = customizationType === 'player' || customizationType === 'user';
    
    this.setCustomizationActive(hasCustomization);
  }

  _hideQuickBuy() {
    if (this.quickBuyContainer) {
      this.quickBuyContainer.style.display = 'none';
    }
  }

  _showQuickBuy() {
    if (this.quickBuyContainer) {
      // Verificar si hay un selector de parche habilitado
      const teamPatchWrapper = document.getElementById('team-patch-selection-wrapper');
      const isPatchSelectorVisible = teamPatchWrapper && teamPatchWrapper.style.display !== 'none';
      
      const enableTeamPatchElement = document.querySelector('[data-enable-team-patch]');
      const enableTeamPatch = enableTeamPatchElement ? enableTeamPatchElement.dataset.enableTeamPatch === 'true' : false;
      
      const teamPatchField = document.querySelector('input[name="properties[Parche de equipo]"]');
      const hasActiveTeamPatch = teamPatchField && teamPatchField.value && teamPatchField.value !== '';
      
      // Ocultar si: el selector está habilitado O visible O hay una selección activa
      if (enableTeamPatch || isPatchSelectorVisible || hasActiveTeamPatch) {
        this.quickBuyContainer.style.display = 'none';
      } else {
        this.quickBuyContainer.style.display = '';
      }
    }
  }

  /**
   * Método público para uso externo
   * Verifica si hay personalización activa
   */
  hasActiveCustomization() {
    return this.isCustomizationActive;
  }

  /**
   * Método público para limpiar personalización
   */
  clearCustomization() {
    this.setCustomizationActive(false);
  }

  /**
   * Gets customization data for Cart Ajax API
   * @returns {Object|null}
   */
  getCustomizationData() {
    if (!this.isCustomizationActive) {
      return null;
    }

    return {
      isFree: this.isFreeCustomization,
      properties: this._getProperties()
      // Note: addonVariantId is now obtained dynamically by NestedCartHandler
      // from window.personalizationProductSettings based on customization type
    };
  }

  /**
   * Extrae las properties de personalización
   * @returns {Object}
   * @private
   */
  _getProperties() {
    const properties = {};

    // 1. Producto Base (Nombre + Variante)
    const addonProducto = document.querySelector('#addon_property_producto');
    if (addonProducto && addonProducto.value) {
      properties['Producto base'] = addonProducto.value;
    } else {
        // Fallback si el input no existe o está vacío
        const productTitle = document.querySelector('h1')?.textContent.trim() || '';
        properties['Producto base'] = productTitle;
    }

    // 2. Nombre (Enviar siempre, incluso vacío, si es la intención del requisito)
    // El requisito dice: "Nombre (vacío en caso de haber escogido la variante de Solo Dorsal)"
    if (this.basePropertyInputs.nombre) {
      properties['Nombre'] = this.basePropertyInputs.nombre.value || "";
    }

    // 3. Dorsal (Enviar siempre, incluso vacío)
    if (this.basePropertyInputs.dorsal) {
      properties['Dorsal'] = this.basePropertyInputs.dorsal.value || "";
    }

    // 4. Parche (Solo si tiene valor, suele ser opcional)
    if (this.basePropertyInputs.parche?.value) {
      properties['Parche de equipo'] = this.basePropertyInputs.parche.value;
    }
    
    return properties;
  }

}

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productCustomizationFormHandler = new ProductCustomizationFormHandler();
  });
} else {
  window.productCustomizationFormHandler = new ProductCustomizationFormHandler();
}

// Exportar para uso en módulos
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductCustomizationFormHandler;
}
